class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        /* Always the whole drawer. Dawn trapped the empty state on
           .drawer__inner-empty, which in this markup holds only the paragraph
           and the link, so the Close button in the header fell outside the
           loop (audit A11Y-15, 2026-09-03). */
        const containerToTrapFocusOn = document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    /* KEEP `is-empty` ON THE HOST IN STEP WITH WHAT WAS JUST RENDERED.
       (Andre, 2026-08-31: adding from a collection page opened a drawer with
       "nothing showing", the panel's content floating in its middle.)

       `is-empty` is written on <cart-drawer> itself, which is OUTSIDE
       #CartDrawer -- the only thing the loop above replaces. So a page that
       LOADED with an empty bag kept the class forever, and two rules then fired
       on a cart that was no longer empty: our own
       `.pulse-cart.is-empty .sc__body { display: none }` hid the lines, and
       Dawn's `cart-drawer.is-empty .drawer__inner { display: grid;
       grid-template-rows: 1fr; align-items: center }` centred the panel
       vertically. The totals still looked right because the footer is not
       inside .sc__body, which is what made it read as a rendering bug rather
       than a stale class.

       Synced here rather than in any one caller because every add path goes
       through renderContents: the PDP's product-form.js, the collection card in
       pulse-cards2.js, and pulse-cart-axel.js. <cart-drawer-items> IS inside
       the replaced markup, so its class is always the fresh answer. */
    const freshItems = this.querySelector('cart-drawer-items');
    if (freshItems) this.classList.toggle('is-empty', freshItems.classList.contains('is-empty'));

    /* WP4-F (2026-09-06). Dawn re-binds the overlay's close listener here, on the assumption
       that renderContents has just replaced it. It has not: renderContents replaces the innerHTML
       of #CartDrawer, and #CartDrawer-Overlay is a SIBLING of it inside <cart-drawer> (see
       snippets/cart-drawer.liquid), so the element the constructor bound is still the live one.
       Every add therefore stacked another `close` handler on the scrim -- and because
       `this.close.bind(this)` is a fresh function each time, they are all distinct listeners
       that removeEventListener could never have collapsed. Three adds meant one click on the
       scrim ran close() three times. The constructor's binding is the only one needed, so this
       is now the open alone. */
    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
